import { useState, useMemo, useCallback } from 'react';
import Button from '../common/Button';
import Input from '../common/Input';
import {
  sortData,
  filterData,
  paginateData,
  convertToCSV,
  downloadCSV
} from '../../utils/csvUtils';
import styles from './CSVDataViewer.module.css';

// Expected member fields for column mapping
const MEMBER_FIELDS = [
  { key: 'email', label: 'Email', required: true },
  { key: 'firstName', label: 'First Name', required: true },
  { key: 'lastName', label: 'Last Name', required: true },
  { key: 'memberId', label: 'Member ID', required: false },
  { key: 'phone', label: 'Phone', required: false },
  { key: 'street', label: 'Street', required: false },
  { key: 'city', label: 'City', required: false },
  { key: 'state', label: 'State', required: false },
  { key: 'zipCode', label: 'ZIP Code', required: false },
  { key: 'dateOfBirth', label: 'Date of Birth', required: false }
];

// Validation helpers
const isValidPhone = (phone) => {
  if (!phone) return true;
  const cleaned = phone.replace(/\D/g, '');
  return cleaned.length === 10 || cleaned.length === 11;
};

const isValidZip = (zip) => {
  if (!zip) return true;
  const cleaned = zip.replace(/\D/g, '');
  return cleaned.length === 5 || cleaned.length === 9;
};

const US_STATES = ['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY','DC'];

const isValidState = (state) => {
  if (!state) return true;
  return US_STATES.includes(state.toUpperCase());
};

// Validate a row and return array of error messages
const validateRow = (row, columnMapping) => {
  const errors = [];
  const mappedRow = {};
  
  Object.entries(columnMapping).forEach(([csvHeader, mappedField]) => {
    if (mappedField && row[csvHeader] !== undefined) {
      mappedRow[mappedField] = row[csvHeader];
    }
  });

  // Required field checks
  if (!mappedRow.email) errors.push('Email required');
  if (!mappedRow.firstName) errors.push('First name required');
  if (!mappedRow.lastName) errors.push('Last name required');
  
  // Format validation
  if (mappedRow.phone && !isValidPhone(mappedRow.phone)) {
    errors.push('Invalid phone');
  }
  if (mappedRow.zipCode && !isValidZip(mappedRow.zipCode)) {
    errors.push('Invalid ZIP');
  }
  if (mappedRow.state && !isValidState(mappedRow.state)) {
    errors.push('Invalid state');
  }

  return errors;
};

// Count dependents in a row
const countDependents = (row) => {
  let count = 0;
  for (let i = 1; i <= 5; i++) {
    const firstName = row[`dependent${i}_firstName`] || row[`dependent${i}_first_name`];
    const lastName = row[`dependent${i}_lastName`] || row[`dependent${i}_last_name`];
    if (firstName && lastName) count++;
  }
  return count;
};

// Extract dependents from a row
const extractDependents = (row) => {
  const dependents = [];
  for (let i = 1; i <= 5; i++) {
    const prefix = `dependent${i}_`;
    const firstName = row[`${prefix}firstName`] || row[`${prefix}first_name`] || '';
    const lastName = row[`${prefix}lastName`] || row[`${prefix}last_name`] || '';
    if (firstName || lastName) {
      dependents.push({
        firstName,
        lastName,
        relationship: row[`${prefix}relationship`] || '',
        dob: row[`${prefix}dob`] || row[`${prefix}dateOfBirth`] || '',
        email: row[`${prefix}email`] || '',
        phone: row[`${prefix}phone`] || ''
      });
    }
  }
  return dependents;
};

// Format address from parts
const formatAddress = (row) => {
  const parts = [
    row.street || row.address,
    row.city,
    row.state,
    row.zipCode || row.zip
  ].filter(Boolean);
  return parts.join(', ') || '—';
};

const CSVDataViewer = ({
  headers = [],
  data = [],
  filename = 'export.csv',
  pageSize = 25,
  showExport = true,
  showSearch = true,
  showColumnToggle = true,
  showPagination = true,
  showColumnMapping = false,
  showValidation = false,
  showDependents = false,
  onColumnMappingChange = null
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortKey, setSortKey] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  const [page, setPage] = useState(1);
  const [hiddenColumns, setHiddenColumns] = useState(new Set());
  const [showColumnMenu, setShowColumnMenu] = useState(false);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [columnMapping, setColumnMapping] = useState(() => {
    // Auto-map matching column names
    const mapping = {};
    headers.forEach((header) => {
      const lowerHeader = header.toLowerCase().replace(/[_\s-]/g, '');
      const match = MEMBER_FIELDS.find((f) => 
        f.key.toLowerCase() === lowerHeader ||
        f.label.toLowerCase().replace(/\s/g, '') === lowerHeader
      );
      if (match) {
        mapping[header] = match.key;
      }
    });
    return mapping;
  });
  const [showMappingPanel, setShowMappingPanel] = useState(false);

  // Computed columns: combine address, add dependents count
  const computedHeaders = useMemo(() => {
    if (!showDependents) return headers;
    
    const baseHeaders = headers.filter(h => !h.startsWith('dependent'));
    // Add computed columns
    if (!baseHeaders.includes('Address') && (headers.includes('street') || headers.includes('address') || headers.includes('city'))) {
      baseHeaders.push('Address');
    }
    if (!baseHeaders.includes('Dependents')) {
      baseHeaders.push('Dependents');
    }
    return baseHeaders;
  }, [headers, showDependents]);

  // Computed data with virtual columns
  const computedData = useMemo(() => {
    if (!showDependents) return data;
    
    return data.map((row) => ({
      ...row,
      Address: formatAddress(row),
      Dependents: countDependents(row)
    }));
  }, [data, showDependents]);

  // Row validation errors
  const rowValidationErrors = useMemo(() => {
    if (!showValidation) return {};
    const errors = {};
    data.forEach((row, idx) => {
      const rowErrors = validateRow(row, columnMapping);
      if (rowErrors.length > 0) {
        errors[idx] = rowErrors;
      }
    });
    return errors;
  }, [data, showValidation, columnMapping]);

  // Visible headers
  const visibleHeaders = useMemo(
    () => computedHeaders.filter((h) => !hiddenColumns.has(h)),
    [computedHeaders, hiddenColumns]
  );

  // Filter data
  const filteredData = useMemo(
    () => filterData(computedData, searchTerm, visibleHeaders),
    [computedData, searchTerm, visibleHeaders]
  );

  // Sort data
  const sortedData = useMemo(
    () => sortData(filteredData, sortKey, sortDirection),
    [filteredData, sortKey, sortDirection]
  );

  // Paginate data
  const paginatedData = useMemo(
    () => paginateData(sortedData, page, pageSize),
    [sortedData, page, pageSize]
  );

  // Total pages
  const totalPages = Math.ceil(sortedData.length / pageSize);

  // Error count
  const errorCount = Object.keys(rowValidationErrors).length;

  // Handle sort
  const handleSort = useCallback((header) => {
    setSortKey((prev) => {
      if (prev === header) {
        setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
        return header;
      }
      setSortDirection('asc');
      return header;
    });
  }, []);

  // Handle column toggle
  const toggleColumn = useCallback((header) => {
    setHiddenColumns((prev) => {
      const next = new Set(prev);
      if (next.has(header)) {
        next.delete(header);
      } else {
        if (next.size < computedHeaders.length - 1) {
          next.add(header);
        }
      }
      return next;
    });
  }, [computedHeaders.length]);

  // Handle row expand
  const toggleRowExpand = useCallback((rowIndex) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(rowIndex)) {
        next.delete(rowIndex);
      } else {
        next.add(rowIndex);
      }
      return next;
    });
  }, []);

  // Handle column mapping change
  const handleMappingChange = useCallback((csvHeader, mappedField) => {
    const newMapping = { ...columnMapping, [csvHeader]: mappedField };
    setColumnMapping(newMapping);
    if (onColumnMappingChange) {
      onColumnMappingChange(newMapping);
    }
  }, [columnMapping, onColumnMappingChange]);

  // Handle export
  const handleExport = useCallback(() => {
    const csvContent = convertToCSV(visibleHeaders, sortedData);
    downloadCSV(csvContent, filename);
  }, [visibleHeaders, sortedData, filename]);

  // Handle search
  const handleSearch = useCallback((e) => {
    setSearchTerm(e.target.value);
    setPage(1);
  }, []);

  if (!headers.length) {
    return (
      <div className={styles.empty}>
        No data to display
      </div>
    );
  }

  return (
    <div className={styles.viewer}>
      {/* Toolbar */}
      <div className={styles.toolbar}>
        <div className={styles.toolbarLeft}>
          {showSearch && (
            <Input
              placeholder="Search all columns..."
              value={searchTerm}
              onChange={handleSearch}
              className={styles.searchInput}
            />
          )}
          <span className={styles.rowCount}>
            {sortedData.length.toLocaleString()} rows
            {searchTerm && ` (filtered from ${data.length.toLocaleString()})`}
          </span>
          {showValidation && errorCount > 0 && (
            <span className={styles.errorBadge}>
              {errorCount} error{errorCount !== 1 ? 's' : ''}
            </span>
          )}
        </div>

        <div className={styles.toolbarRight}>
          {showColumnMapping && (
            <button
              className={`${styles.columnToggleBtn} ${showMappingPanel ? styles.active : ''}`}
              onClick={() => setShowMappingPanel(!showMappingPanel)}
              title="Map columns"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 3v18M3 12h18M8 8l4-4 4 4M8 16l4 4 4-4" />
              </svg>
              Map Columns
            </button>
          )}

          {showColumnToggle && (
            <div className={styles.columnToggle}>
              <button
                className={styles.columnToggleBtn}
                onClick={() => setShowColumnMenu(!showColumnMenu)}
                title="Toggle columns"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="3" width="7" height="7" />
                  <rect x="14" y="3" width="7" height="7" />
                  <rect x="3" y="14" width="7" height="7" />
                  <rect x="14" y="14" width="7" height="7" />
                </svg>
                Columns
              </button>
              {showColumnMenu && (
                <div className={styles.columnMenu}>
                  <div className={styles.columnMenuHeader}>
                    Show/Hide Columns
                  </div>
                  <div className={styles.columnMenuList}>
                    {computedHeaders.map((header) => (
                      <label key={header} className={styles.columnMenuItem}>
                        <input
                          type="checkbox"
                          checked={!hiddenColumns.has(header)}
                          onChange={() => toggleColumn(header)}
                        />
                        <span>{header}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {showExport && (
            <Button
              variant="outline"
              size="small"
              onClick={handleExport}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
              Export CSV
            </Button>
          )}
        </div>
      </div>

      {/* Column Mapping Panel */}
      {showColumnMapping && showMappingPanel && (
        <div className={styles.mappingPanel}>
          <div className={styles.mappingHeader}>
            <h4>Column Mapping</h4>
            <p>Map your CSV columns to member fields</p>
          </div>
          <div className={styles.mappingGrid}>
            {headers.filter(h => !h.startsWith('dependent')).map((header) => (
              <div key={header} className={styles.mappingItem}>
                <span className={styles.csvColumn}>{header}</span>
                <span className={styles.mappingArrow}>→</span>
                <select
                  value={columnMapping[header] || ''}
                  onChange={(e) => handleMappingChange(header, e.target.value)}
                  className={styles.mappingSelect}
                >
                  <option value="">— Skip —</option>
                  {MEMBER_FIELDS.map((field) => (
                    <option key={field.key} value={field.key}>
                      {field.label}{field.required ? ' *' : ''}
                    </option>
                  ))}
                </select>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Table */}
      <div className={styles.tableWrapper}>
        <table className={styles.table}>
          <thead>
            <tr>
              {showDependents && <th className={styles.expandCol}></th>}
              {visibleHeaders.map((header) => (
                <th
                  key={header}
                  onClick={() => handleSort(header)}
                  className={sortKey === header ? styles.sortActive : ''}
                >
                  <div className={styles.headerContent}>
                    <span>{header}</span>
                    <span className={styles.sortIcon}>
                      {sortKey === header && (
                        sortDirection === 'asc' ? '↑' : '↓'
                      )}
                    </span>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {paginatedData.map((row, rowIndex) => {
              const globalIndex = (page - 1) * pageSize + rowIndex;
              const errors = rowValidationErrors[globalIndex];
              const hasErrors = errors && errors.length > 0;
              const dependents = showDependents ? extractDependents(row) : [];
              const isExpanded = expandedRows.has(globalIndex);

              return (
                <>
                  <tr
                    key={globalIndex}
                    className={`${hasErrors ? styles.errorRow : ''} ${isExpanded ? styles.expandedRow : ''}`}
                  >
                    {showDependents && (
                      <td className={styles.expandCell}>
                        {dependents.length > 0 && (
                          <button
                            className={styles.expandBtn}
                            onClick={() => toggleRowExpand(globalIndex)}
                            title={isExpanded ? 'Collapse' : 'Expand dependents'}
                          >
                            {isExpanded ? '−' : '+'}
                          </button>
                        )}
                      </td>
                    )}
                    {visibleHeaders.map((header) => (
                      <td
                        key={header}
                        className={header === 'Dependents' && row[header] > 0 ? styles.dependentCount : ''}
                      >
                        {header === 'Dependents' ? (
                          row[header] > 0 ? row[header] : '—'
                        ) : (
                          row[header] ?? ''
                        )}
                      </td>
                    ))}
                  </tr>
                  {/* Expanded dependent details */}
                  {isExpanded && dependents.length > 0 && (
                    <tr className={styles.expandedDetail}>
                      <td colSpan={visibleHeaders.length + 1}>
                        <div className={styles.dependentDetails}>
                          <h5>Dependents ({dependents.length})</h5>
                          <table className={styles.dependentTable}>
                            <thead>
                              <tr>
                                <th>Name</th>
                                <th>Relationship</th>
                                <th>DOB</th>
                                <th>Email</th>
                                <th>Phone</th>
                              </tr>
                            </thead>
                            <tbody>
                              {dependents.map((dep, idx) => (
                                <tr key={idx}>
                                  <td>{dep.firstName} {dep.lastName}</td>
                                  <td>{dep.relationship || '—'}</td>
                                  <td>{dep.dob || '—'}</td>
                                  <td>{dep.email || '—'}</td>
                                  <td>{dep.phone || '—'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </td>
                    </tr>
                  )}
                  {/* Error details */}
                  {hasErrors && (
                    <tr className={styles.errorDetail}>
                      <td colSpan={visibleHeaders.length + (showDependents ? 1 : 0)}>
                        <div className={styles.errorList}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="12" cy="12" r="10" />
                            <line x1="12" y1="8" x2="12" y2="12" />
                            <line x1="12" y1="16" x2="12.01" y2="16" />
                          </svg>
                          {errors.join(' • ')}
                        </div>
                      </td>
                    </tr>
                  )}
                </>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Empty state */}
      {paginatedData.length === 0 && (
        <div className={styles.emptyState}>
          {searchTerm
            ? 'No rows match your search'
            : 'No data available'
          }
        </div>
      )}

      {/* Pagination */}
      {showPagination && totalPages > 1 && (
        <div className={styles.pagination}>
          <Button
            variant="outline"
            size="small"
            disabled={page <= 1}
            onClick={() => setPage(1)}
            title="First page"
          >
            «
          </Button>
          <Button
            variant="outline"
            size="small"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            Previous
          </Button>
          <span className={styles.pageInfo}>
            Page {page} of {totalPages}
          </span>
          <Button
            variant="outline"
            size="small"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          >
            Next
          </Button>
          <Button
            variant="outline"
            size="small"
            disabled={page >= totalPages}
            onClick={() => setPage(totalPages)}
            title="Last page"
          >
            »
          </Button>
        </div>
      )}
    </div>
  );
};

export default CSVDataViewer;
